import {
  Grid2 as Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

export const SubAdminDomains = ({
  title,
  subAdminDomainsRows,
  setManagementData,
  isSm,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [domain, setDomain] = useState("");
  const [errorText, setErrorText] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
        setStatusIsError(false);
      }
    }, 3500);

    return () => {
      clearInterval(clearTextInterval);
    };
  }, [errorText]);

  const rows = useMemo(() => {
    return (subAdminDomainsRows || []).map((item, index) => ({
      id: item?.id || String(index),
      domain: item?.domain || "",
    }));
  }, [subAdminDomainsRows]);

  const refreshManagementData = async () => {
    const res = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
      { currentSession },
      { withCredentials: true },
    );

    setManagementData(res?.data?.data);
  };

  const handleAdd = async () => {
    const normalizedDomain = (domain || "").trim().toLowerCase();

    if (!normalizedDomain) {
      setErrorText("Domain is required.");
      setStatusIsError(true);
      return;
    }

    if (!domainRegex.test(normalizedDomain)) {
      setErrorText("Please enter a valid domain (e.g. google.com).");
      setStatusIsError(true);
      return;
    }

    try {
      setAddLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/sub-admin-domains/add`,
        {
          currentSession,
          domain: normalizedDomain,
        },
        { withCredentials: true },
      );

      await refreshManagementData();

      setDomain("");
      setErrorText(resp?.data?.message || "Domain added.");
      setStatusIsError(false);
    } catch (e) {
      setErrorText(e?.response?.data?.message || "Couldn't add domain.");
      setStatusIsError(true);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedRow?.domain) return;

    try {
      setRemoveLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/sub-admin-domains/remove`,
        {
          currentSession,
          domain: selectedRow.domain,
        },
        { withCredentials: true },
      );

      await refreshManagementData();

      setErrorText(resp?.data?.message || "Domain removed.");
      setStatusIsError(false);
      setSelectedRow(null);
      setConfirmOpen(false);
    } catch (e) {
      setErrorText(e?.response?.data?.message || "Couldn't remove domain.");
      setStatusIsError(true);
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns = [
    {
      field: "domain",
      headerName: "Domain",
      flex: 1,
      minWidth: 280,
      headerClassName: "management-datagrid-title",
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      sortable: false,
      filterable: false,
      flex: 0.35,
      align: "center",
      headerAlign: "center",
      headerClassName: "management-datagrid-title",
      getActions: (params) => [
        <GridActionsCellItem
          key={`remove-${params.id}`}
          icon={<DeleteOutlineIcon sx={{ color: "error.main" }} />}
          label="Remove"
          disabled={removeLoading || addLoading}
          onClick={() => {
            setSelectedRow(params.row);
            setConfirmOpen(true);
          }}
        />,
      ],
    },
  ];

  return (
    <>
      <DataGrid
        sx={{
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          minHeight: "21rem",
        }}
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        disableRowSelectionOnClick
      />

      <Grid container spacing={isSm ? 1 : 2} alignItems="center" sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 9 }}>
          <TextField
            label="Domain"
            variant="filled"
            fullWidth
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={addLoading || removeLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <LoadingButton
            loading={addLoading}
            disabled={!domain || addLoading || removeLoading}
            fullWidth
            variant="contained"
            onClick={handleAdd}
          >
            Add
          </LoadingButton>
        </Grid>
      </Grid>

      <Typography
        variant="body1"
        color={statusIsError ? "error" : "primary"}
        fontWeight="bold"
        textAlign="center"
        sx={{ mt: "1rem", minHeight: "1.5rem" }}
      >
        {errorText}
      </Typography>

      <Dialog
        open={confirmOpen}
        onClose={removeLoading ? () => {} : () => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove sub-admin domain{" "}
            <strong>{selectedRow?.domain}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={removeLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleRemove}
            loading={removeLoading}
            variant="contained"
            color="error"
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
